import React from 'react';
import { Link } from 'react-router-dom';

/*
 * The portal's surfaces, and the rule that keeps them apart.
 *
 *   Panel  #FFFFFF  a white container on a hairline. The default: lists,
 *                   tables, activity, anything holding rows.
 *   Card   #F3F3F1  the quiet block. An inset panel inside a Panel, or a
 *                   secondary strip on the canvas.
 *   Hero   #000000  the one editorial moment a screen is allowed.
 *
 * The page canvas behind all of them is #F7F7F5. A white Panel on a white
 * canvas is the fastest way to lose the structure of a screen, which is why
 * the canvas is never white.
 */

const PAD: Record<string, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
};

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  pad?: keyof typeof PAD;
}

export const Card: React.FC<CardProps> = ({ pad = 'lg', className = '', children, ...rest }) => (
  <div className={`rounded-card bg-canvas-inset ${PAD[pad]} ${className}`} {...rest}>{children}</div>
);

export interface PanelProps extends CardProps {
  /*
   * Hold the audit's 238px card floor. Two cards side by side carry different
   * numbers of rows, and without a floor the shorter one collapses and the
   * grid stops reading as a pair.
   */
  floor?: boolean;
}

export const Panel: React.FC<PanelProps> = ({ pad = 'none', floor = false, className = '', children, ...rest }) => {
  const height = { true: 'min-h-[238px]', false: '' }[String(floor)];
  return (
    <div className={`overflow-hidden rounded-panel border border-line bg-white ${height} ${PAD[pad]} ${className}`} {...rest}>
      {children}
    </div>
  );
};

/** The black editorial card. One per screen at most. */
export const Hero: React.FC<CardProps> = ({ pad = 'lg', className = '', children, ...rest }) => (
  <div className={`rounded-card bg-black text-white ${PAD[pad]} ${className}`} {...rest}>{children}</div>
);

/*
 * The mono kicker above a title. It takes the eyebrow colour, or yellow on a
 * black surface: those are the only two it is ever set in.
 */
export const Kicker: React.FC<{ tone?: 'brand' | 'accent' | 'muted'; className?: string; children: React.ReactNode }> = ({
  tone = 'brand', className = '', children,
}) => {
  const color = { brand: 'text-eyebrow', accent: 'text-accent-500', muted: 'text-ink-soft' }[tone];
  return (
    <div className={`text-[11px] font-semibold uppercase leading-[17.4px] tracking-[0.14em] ${color} ${className}`}>
      {children}
    </div>
  );
};

/*
 * A panel with the standard head: kicker, heading, one line of orientation,
 * and one control on the right (a search field, a "See all", a filter).
 */
export interface PanelHeadProps {
  kicker?: React.ReactNode;
  title?: React.ReactNode;
  desc?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export const PanelHead: React.FC<PanelHeadProps> = ({ kicker, title, desc, action, className = '' }) => (
  <div className={`px-5 pt-5 md:px-6 md:pt-6 ${className}`}>
    <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        {kicker && <Kicker className="mb-2">{kicker}</Kicker>}
        {title && <h2 className="m-0 text-[20px] font-bold leading-[28px] tracking-[-0.005em] text-ink">{title}</h2>}
      </div>
      {action && <div className="shrink-0 pb-0.5">{action}</div>}
    </div>
    {desc && <p className="mt-2 text-[13px] leading-[21.5px] text-ink-dim">{desc}</p>}
    {/* The rule sits inside the panel's padding, not across it: it separates
        the head from its own rows, and a full-bleed line would read as the
        edge of the panel instead. */}
    <div className="mt-3.5 border-b border-line-soft" />
  </div>
);


/*
 * Banner: an icon tile, a kicker, a title, and one action, on a full-width
 * strip above or below the cards.
 *
 * The audit measures this same shape three times on Home at three heights —
 * the Orbit hero at 115px, the AI Adoption strip at 98px, and shared patterns
 * at 65px — so it is one component with a size, not three hand-built rows.
 * `tone` picks the surface: white on a hairline for the hero, the quiet inset
 * block for a secondary strip.
 */
const BANNER_SIZE: Record<string, string> = {
  sm: 'min-h-[65px] px-5 py-4 md:px-6',
  md: 'min-h-[98px] px-5 py-5 md:px-6',
  lg: 'min-h-[115px] px-5 py-5 md:px-[24px]',
};

// The title tracks the height. An 18px heading inside a 65px strip is a
// heading with nowhere to sit, and it makes a secondary row shout.
const BANNER_TITLE: Record<string, string> = {
  sm: 'text-[13px] font-semibold leading-[18.9px]',
  md: 'text-[16px] font-bold leading-[22.8px]',
  lg: 'text-[18px] font-bold leading-[25.4px]',
};

const BANNER_GAP: Record<string, string> = {
  sm: 'gap-3.5',
  md: 'gap-[21px]',
  lg: 'gap-[21px]',
};

const BANNER_TONE: Record<string, string> = {
  surface: 'rounded-hero border border-line bg-white',
  quiet: 'rounded-hero bg-canvas-inset',
};

export interface BannerProps {
  mark?: React.ReactNode;
  kicker?: React.ReactNode;
  title: React.ReactNode;
  desc?: React.ReactNode;
  action?: React.ReactNode;
  size?: keyof typeof BANNER_SIZE;
  tone?: keyof typeof BANNER_TONE;
  to?: string;
  onClick?: () => void;
  className?: string;
}

export const Banner: React.FC<BannerProps> = ({
  mark, kicker, title, desc, action, size = 'md', tone = 'surface', to, onClick, className = '',
}) => {
  const shell = `flex w-full flex-wrap items-center justify-between gap-4 text-left ${BANNER_TONE[tone]} ${BANNER_SIZE[size]} ${className}`;
  const body = (
    <>
      <div className={`flex min-w-0 items-center ${BANNER_GAP[size]}`}>
        {mark}
        <div className="min-w-0">
          {kicker && <Kicker className="mb-1">{kicker}</Kicker>}
          <div className={`text-ink ${BANNER_TITLE[size]}`}>{title}</div>
          {desc && <div className="mt-0.5 text-[12.5px] leading-[20.75px] text-ink-dim">{desc}</div>}
        </div>
      </div>
      {action && <div className="max-w-full shrink-0">{action}</div>}
    </>
  );

  if (to) return <Link to={to} className={`${shell} transition-colors hover:brightness-[0.985]`}>{body}</Link>;
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${shell} transition-colors hover:brightness-[0.985]`}>
        {body}
      </button>
    );
  }
  return <div className={shell}>{body}</div>;
};

/** Hairline-separated stack. The row divider is lighter than a panel border. */
export const Divided: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`divide-y divide-line-soft ${className}`}>{children}</div>
);
